<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import ListMarkers from '../components/ListMarkers.vue';
import MenuDesktop from '../components/MenuDesktop.vue';
import MenuMobile from '../components/MenuMobile.vue';
import TopLinks from '../components/TopLinks.vue';
import getMarkersCategory from '../lib/getMarkersCategory';
import Category from '../model/category';
import { useRoute } from 'vue-router';
import { setActiveSection, setActiveSubSection } from '../store/Navigation';

const props = defineProps({
    category: {
        type: String,
        required: true
    }
});

const category = ref<Category>();

watch(() => props.category, async () => {
    category.value = await getMarkersCategory(props.category);
}, { immediate: true });

const route = useRoute();

const findElementBySlug = (slug: string): HTMLElement | null => {
    const elements = document.querySelectorAll<HTMLElement>('[data-anchor-slug]');
    for (const el of elements) {
        if (el.dataset.anchorSlug === slug) {
            return el;
        }
    }
    return null;
};

const ensureNavFocus = (slug: string) => {
    const parts = slug.split('.');
    if (parts.length >= 2) {
        setActiveSection(`${parts[0]}.${parts[1]}`);
    }
    if (parts.length >= 3) {
        setActiveSubSection(`${parts[0]}.${parts[1]}.${parts[2]}`);
    } else {
        setActiveSubSection(null);
    }
};

const scrollToSlug = (slug: string, options: { behavior?: ScrollBehavior; highlight?: boolean } = {}) => {
    if (!slug) return;
    const element = findElementBySlug(slug) || document.getElementById(slug);
    if (!element) return;

    const offset = 140;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
        top,
        behavior: options.behavior ?? 'smooth',
    });

    ensureNavFocus(slug);

    if (options.highlight) {
        element.classList.add('highlight-search');
        window.setTimeout(() => {
            element.classList.remove('highlight-search');
        }, 2000);
    }
};


const search = ref("");
const showSearchDropdown = ref(false);

// Fonction pour extraire tous les marqueurs disponibles
const extractAllMarkers = (schema: any, slug: string = "d", results: Array<{ label: string; slug: string; description: string }> = []): Array<{ label: string; slug: string; description: string }> => {
    if (!schema) return results;

    if (schema.description) {
        // C'est un marqueur final
        const labelPath = slug.split("[i]").join("").split(".").slice(1);
        const label = labelPath.join(" › ");
        results.push({
            label: label,
            slug: slug,
            description: schema.description
        });
    }

    if (schema.properties) {
        for (const key in schema.properties) {
            extractAllMarkers(schema.properties[key], `${slug}.${key}`, results);
        }
    }

    if (schema.items) {
        extractAllMarkers(schema.items, `${slug}[i]`, results);
    }

    return results;
};

// Recherche dans tous les marqueurs
const searchInMarkers = computed(() => {
    const normalizedQuery = search.value.trim().toLowerCase();
    if (normalizedQuery.length < 2) {
        return [];
    }

    const allMarkers = extractAllMarkers(category.value?.d);

    return allMarkers
        .filter(m =>
            m.description.toLowerCase().includes(normalizedQuery) ||
            m.label.toLowerCase().includes(normalizedQuery) ||
            m.slug.toLowerCase().includes(normalizedQuery)
        )
        .slice(0, 10); // Limiter à 10 résultats
});

const markersTree = computed(() => category.value?.d);

// Navigation vers un marqueur spécifique
const navigateToMarker = (slug: string) => {
    search.value = "";
    showSearchDropdown.value = false;

    // Ouvrir les sections parentes si nécessaire
    // Émettre un événement global pour ouvrir toutes les sections "Marqueurs supplémentaires"
    window.dispatchEvent(new CustomEvent('expand-optional-markers', { detail: { slug } }));

    // Attendre que le DOM soit mis à jour avant de scroller
    nextTick(() => {
        // Petit délai supplémentaire pour l'animation d'expansion
        window.setTimeout(() => {
            scrollToSlug(slug, { highlight: true });
        }, 100);
    });
};

// Fonction pour gérer le blur du champ de recherche
const handleSearchBlur = () => {
    window.setTimeout(() => {
        showSearchDropdown.value = false;
    }, 200);
};

const getClosestElement = (elements: HTMLElement[]): HTMLElement | null => {
    if (!elements.length) return null;
    const offset = 160;
    let closest: HTMLElement | null = null;
    let minDistance = Infinity;

    elements.forEach((element) => {
        const distance = Math.abs(element.getBoundingClientRect().top - offset);
        if (distance < minDistance) {
            minDistance = distance;
            closest = element;
        }
    });

    return closest ?? null;
};

const updateActiveSections = () => {
    const sectionElements = Array.from(document.querySelectorAll<HTMLElement>('[data-anchor-level="2"]'));
    if (!sectionElements.length) {
        setActiveSection(null);
        setActiveSubSection(null);
        return;
    }

    const closestSection = getClosestElement(sectionElements) ?? sectionElements[0];
    const sectionSlug = closestSection.dataset.anchorSlug ?? null;
    setActiveSection(sectionSlug);

    const subsectionElements = Array.from(document.querySelectorAll<HTMLElement>('[data-anchor-level="3"]'));
    if (sectionSlug) {
        const relevantSubs = subsectionElements.filter((el) =>
            (el.dataset.anchorSlug ?? '').startsWith(`${sectionSlug}.`)
        );

        if (relevantSubs.length) {
            const closestSub = getClosestElement(relevantSubs);
            setActiveSubSection(closestSub?.dataset.anchorSlug ?? null);
            return;
        }
    }

    setActiveSubSection(null);
};

const handleScroll = () => {
    window.requestAnimationFrame(updateActiveSections);
};

const handleResize = () => {
    window.requestAnimationFrame(updateActiveSections);
};

onMounted(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    nextTick(() => updateActiveSections());
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleResize);
});

watch(() => category.value, () => {
    nextTick(() => {
        if (!category.value) return;
        updateActiveSections();
        if (route.hash) {
            scrollToSlug(route.hash.slice(1), { behavior: 'auto' });
        }
    });
});

watch(() => route.hash, (newHash) => {
    if (newHash) {
        nextTick(() => scrollToSlug(newHash.slice(1), { behavior: 'smooth' }));
    }
});

</script>
<template>
    <ul v-if="category" class="xl:w-9/12 xl:ml-0 xl:float-left xl:pr-8">
        <!-- Barre de recherche avec dropdown -->
        <div class="
            w-full
            sticky
            top-16
            bg-white
            dark:bg-gray-900
            h-auto
            z-30
            -mt-5
            pb-3
          ">
            <div class="relative xl:mt-6 mt-2 xl:mb-4 mb-2">
              <!-- Icône de recherche -->
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <fa :icon="['fas', 'search']" class="text-gray-400 dark:text-gray-500 text-sm" />
              </div>

              <input
                  class="
                      w-full
                      pl-10
                      pr-10
                      py-2.5
                      rounded-lg
                      bg-white
                      dark:bg-gray-800
                      border
                      border-gray-300
                      dark:border-gray-700
                      focus:border-primary-500
                      dark:focus:border-primary-500
                      focus:ring-2
                      focus:ring-primary-200
                      dark:focus:ring-primary-900/30
                      outline-none
                      text-gray-900
                      dark:text-gray-100
                      placeholder-gray-500
                      dark:placeholder-gray-500
                      transition-colors
                      duration-150
                  "
                  type="text"
                  placeholder="Rechercher un marqueur (min. 2 caractères)..."
                  v-model="search"
                  @focus="showSearchDropdown = true"
                  @blur="handleSearchBlur"
              />

              <!-- Bouton clear -->
              <button
                v-if="search"
                @click="search = ''; showSearchDropdown = false"
                class="
                  absolute
                  inset-y-0
                  right-0
                  pr-3
                  flex
                  items-center
                  text-gray-400
                  hover:text-gray-600
                  dark:hover:text-gray-300
                  transition-colors
                  cursor-pointer
                "
                type="button"
                aria-label="Effacer la recherche"
              >
                <fa :icon="['fas', 'times']" class="text-sm" />
              </button>

              <!-- Dropdown des résultats -->
              <div
                v-if="showSearchDropdown && search && search.length >= 2 && searchInMarkers.length > 0"
                class="
                  absolute
                  top-full
                  left-0
                  right-0
                  mt-2
                  bg-white
                  dark:bg-gray-800
                  border
                  border-gray-300
                  dark:border-gray-700
                  rounded-lg
                  shadow-xl
                  max-h-96
                  overflow-y-auto
                  z-50
                "
              >
                <div class="p-2">
                  <p class="px-3 py-2 text-xs text-gray-500 dark:text-gray-500 font-medium uppercase">
                    {{ searchInMarkers.length }} résultat{{ searchInMarkers.length > 1 ? 's' : '' }}
                  </p>

                  <button
                    v-for="(result, index) in searchInMarkers"
                    :key="index"
                    @click="navigateToMarker(result.slug)"
                    class="
                      w-full
                      text-left
                      px-3
                      py-2.5
                      rounded
                      hover:bg-primary-50
                      dark:hover:bg-primary-900/10
                      transition-colors
                      duration-150
                      cursor-pointer
                      group
                    "
                  >
                    <div class="flex items-start gap-2">
                      <fa :icon="['fas', 'arrow-right']" class="text-primary-600 dark:text-primary-400 mt-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {{ result.description }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                          {{ result.label }}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <!-- Message si aucun résultat -->
              <div
                v-if="showSearchDropdown && search && search.length >= 2 && searchInMarkers.length === 0"
                class="
                  absolute
                  top-full
                  left-0
                  right-0
                  mt-2
                  p-3
                  bg-white
                  dark:bg-gray-800
                  border
                  border-gray-300
                  dark:border-gray-700
                  rounded-lg
                  text-sm
                  text-gray-600
                  dark:text-gray-400
                  shadow-lg
                "
              >
                Aucun marqueur trouvé pour "{{ search }}"
              </div>
            </div>
        </div>

        <!-- Menu mobile -->
        <MenuMobile :category="category" />

        <!-- Liens de navigation -->
        <TopLinks :marker="category" />

        <!-- Titre de la catégorie simple -->
        <h1 class="
            mt-4
            mb-6
            text-gray-900
            dark:text-gray-100
            font-semibold
          " :class="['facture', 'devis'].includes(category?.name) ? 'xl:mt-16' : 'xl:mt-0'">
            {{ category.name }}
        </h1>

        <!-- Liste des marqueurs -->
        <ListMarkers v-if="markersTree" :marker="markersTree" slug="d" />
    </ul>

    <!-- Menu desktop -->
    <MenuDesktop v-if="category" :category="category" />
</template>